import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/config";

const KEY = "vision-admin-token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const authHeaders = { headers: { "X-Admin-Token": token } };

  const loadStatus = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/sync-status`, {
        headers: { "X-Admin-Token": t },
      });
      setStatus(data);
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (token) loadStatus(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setBusy(true);
    setMsg("");
    try {
      await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { "X-Admin-Token": input.trim() } },
      );
      localStorage.setItem(KEY, input.trim());
      setToken(input.trim());
      setAuthed(true);
      await loadStatus(input.trim());
    } catch {
      setMsg("Invalid admin token.");
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(KEY);
    setToken("");
    setAuthed(false);
    setInput("");
    setStatus(null);
  };

  const runSync = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/sync`, {}, authHeaders);
      setStatus(data);
      setMsg("Sync complete.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#e8ecef] font-light">
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <p className="text-[11px] tracking-[0.42em] uppercase text-[#8a9498] mb-6">
          VISION · Admin
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-[#e8ecef]">
          Storefront Sync
        </h1>
        <p className="mt-4 text-[#8a9498] text-sm md:text-base leading-relaxed max-w-lg">
          The Vision catalog mirrors your Payhip store automatically every hour.
          Use this panel to force an immediate sync or check the last run.
        </p>

        {!authed && (
          <form onSubmit={signIn} className="mt-14 max-w-md" data-testid="admin-signin-form">
            <label className="text-[11px] tracking-[0.3em] uppercase text-[#8a9498] block mb-4">
              Admin Token
            </label>
            <div className="flex items-center border-b border-[#2a343a] focus-within:border-[#e8ecef] transition-colors duration-500">
              <input
                type="password"
                autoComplete="off"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Paste your admin token"
                data-testid="admin-token-input"
                className="flex-1 bg-transparent border-0 outline-none py-3 text-[#e8ecef] placeholder:text-[#3a444a] text-base"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="admin-signin-button"
                className="ml-4 text-[11px] tracking-[0.28em] uppercase text-[#e8ecef] disabled:opacity-50"
              >
                {busy ? "Verifying" : "Sign in"}
              </button>
            </div>
            {msg && (
              <p className="mt-4 text-sm text-red-300/85" data-testid="admin-error">
                {msg}
              </p>
            )}
          </form>
        )}

        {authed && status && (
          <div className="mt-14 space-y-10">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#8a9498] mb-2">
                  Products
                </p>
                <p className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="admin-product-count">
                  {status.product_count ?? 0}
                </p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#8a9498] mb-2">
                  In Stock
                </p>
                <p className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="admin-instock-count">
                  {status.in_stock_count ?? 0}
                </p>
              </div>
            </div>

            <div className="border-t border-[#2a343a] pt-8 space-y-3 text-sm">
              <p>
                <span className="text-[#8a9498] uppercase tracking-[0.2em] text-[10px] mr-3">
                  Last sync
                </span>
                <span data-testid="admin-last-sync">
                  {status.last_sync_at
                    ? new Date(status.last_sync_at).toLocaleString()
                    : "Never"}
                </span>
              </p>
              <p>
                <span className="text-[#8a9498] uppercase tracking-[0.2em] text-[10px] mr-3">
                  Status
                </span>
                <span data-testid="admin-status">{status.last_sync_status}</span>
              </p>
              <p>
                <span className="text-[#8a9498] uppercase tracking-[0.2em] text-[10px] mr-3">
                  Source
                </span>
                <a
                  href={status.store_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-[#e8ecef]"
                >
                  {status.store_url}
                </a>
              </p>
              {status.last_error && (
                <p className="text-red-300/80 text-xs">
                  Last error: {status.last_error}
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <button
                onClick={runSync}
                disabled={busy}
                data-testid="admin-sync-button"
                className="bg-[#e8ecef] text-[#0a0a0a] px-8 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[#c9d3d8] transition-colors duration-500 disabled:opacity-60"
              >
                {busy ? "Syncing" : "Sync Products Now"}
              </button>
              <button
                onClick={signOut}
                data-testid="admin-signout-button"
                className="border border-[#2a343a] px-8 py-3 text-[11px] tracking-[0.3em] uppercase hover:border-[#e8ecef] transition-colors duration-500"
              >
                Sign out
              </button>
            </div>
            {msg && (
              <p className="text-sm text-[#8a9498]" data-testid="admin-message">
                {msg}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
